// 关卡格数（不让玩家选）：每个地点固定一个 N×N，由"第几座城 + 城内第几个"决定。
// 城内走一条起伏的波浪（难一点 → 缓一下 → 再难），每座城的底数比上一座高一档，
// 于是整条旅程是"循环上升"的：巴黎 4→7，京都 5→8，北欧 6→9，最后一关 9×9。

export const MIN_N = 4;
export const MAX_N = 9;
/** 任何一边最多几格（塔罗竖版最大 7×10） */
export const MAX_SIDE = 10;

/** 棋盘列数 × 行数（方形城市 cols = rows = n） */
export type Grid = { cols: number; rows: number };
export const square = (n: number): Grid => ({ cols: n, rows: n });
/** 成绩 / 统计的键：方形 '6'（与旧存档相同），长方形 '7x10' */
export const gridKey = (g: Grid): string => (g.cols === g.rows ? String(g.cols) : `${g.cols}x${g.rows}`);
/** 显示用：6×6、7×10 */
export const gridLabel = (g: Grid): string => `${g.cols}×${g.rows}`;
export const sameGrid = (a: Grid, b: Grid): boolean => a.cols === b.cols && a.rows === b.rows;

/** 城内 8 关的起伏（加在该城底数上）：0 1 0 1 2 1 2 3 */
export const CITY_WAVE = [0, 1, 0, 1, 2, 1, 2, 3] as const;

/** cityIndex = 城市按 order 排好后的下标（0 = 第一座城）；unlockOrder = 城内顺序 0..7 */
export function levelSize(cityIndex: number, unlockOrder: number): number {
  const wave = CITY_WAVE[((unlockOrder % CITY_WAVE.length) + CITY_WAVE.length) % CITY_WAVE.length];
  return Math.min(MAX_N, Math.max(MIN_N, MIN_N + cityIndex + wave));
}

// ── 塔罗（竖版 2:3 的牌面，22 张，对所有人开放；表按 unlockOrder = 牌号 0..21） ──
// 格子要接近正方形：4×6、6×9 正好方，5×7（0.93）、7×10（0.95）略高一点。
// 难度走波浪：每章里难一点 → 缓一下 → 再难，三章的底数一章比一章高，第 21 张（世界）最难：7×10 + 旋转块。
// 玩法很轻：第一章几乎没有（只有 5 旋转、7 翻面各一次，正好在前面学过的城市玩法之后复习），后两章旋转 / 翻面 / 无交替。

const A: Grid = { cols: 4, rows: 6 }; // 24 块
const B: Grid = { cols: 5, rows: 7 }; // 35 块
const C: Grid = { cols: 6, rows: 9 }; // 54 块
const D: Grid = { cols: 7, rows: 10 }; // 70 块

/** 下标 = unlockOrder（牌号） */
export const TAROT_GRIDS: readonly Grid[] = [
  // 第一章 启程 0–7
  A, A, B, A, B, B, C, B,
  // 第二章 试炼 8–14
  B, C, B, C, C, D, C,
  // 第三章 星辰 15–21
  C, D, C, D, C, D, D,
];

// LevelMods 与 NONE/ROT/FLIP 定义在下面（城市玩法表旁边），塔罗玩法表 TAROT_MODS 也在那里

/** 每日挑战按星期循环：周一、二 5×5，周三、四 6×6，周五、六 7×7，周日 8×8 */
export const DAILY_WEEK = [8, 5, 5, 6, 6, 7, 7] as const; // 下标 = getUTCDay()，0 = 周日

export function dailySize(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  return DAILY_WEEK[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
}

// ── 每城一个新玩法，在后面几关循环出现（v2 后续：用户认可"每城一个新玩法 + 1–3 星"） ──
// 巴黎：纯拼图（教会拖动 / 粘连）。
// 京都：引入"旋转块"——城内第 0、2、4、6 关有旋转块，1、3、5、7 关没有（喘口气）。
// 北欧：引入"背面块"，并让旋转块回来：翻面、旋转、翻面、无、翻面、旋转、翻面、翻面。
// 表里不会同时开两种，但代码允许（PlaySession / BoardScene 都按两种可叠加实现）。

export type LevelMods = { rotate: boolean; flip: boolean };
const NONE: LevelMods = { rotate: false, flip: false };
const ROT: LevelMods = { rotate: true, flip: false };
const FLIP: LevelMods = { rotate: false, flip: true };

/** 下标 = cityIndex（城市按 order 排好），再下标 = unlockOrder */
export const CITY_MODS: readonly (readonly LevelMods[])[] = [
  [NONE, NONE, NONE, NONE, NONE, NONE, NONE, NONE],
  [ROT, NONE, ROT, NONE, ROT, NONE, ROT, NONE],
  [FLIP, ROT, FLIP, NONE, FLIP, ROT, FLIP, FLIP],
];

export function levelMods(cityIndex: number, unlockOrder: number): LevelMods {
  const row = CITY_MODS[cityIndex];
  if (!row) return { ...NONE };
  return { ...(row[((unlockOrder % row.length) + row.length) % row.length] ?? NONE) };
}

/** 塔罗 22 张的玩法，下标 = 牌号 */
export const TAROT_MODS: readonly LevelMods[] = [
  // 第一章 0–7：几乎都是纯拼图
  NONE, NONE, NONE, NONE, NONE, ROT, NONE, FLIP,
  // 第二章 8–14
  NONE, ROT, FLIP, NONE, ROT, NONE, FLIP,
  // 第三章 15–21
  ROT, NONE, FLIP, ROT, NONE, FLIP, ROT,
];

// ── 按主题查表（关卡表的唯一入口）：有自己表的主题（塔罗）查自己的表，其余按城市 order 走方形波浪 ──

type ThemeTable = { grids: readonly Grid[]; mods: readonly LevelMods[] };
export const THEME_LEVELS: Readonly<Record<string, ThemeTable>> = {
  tarot: { grids: TAROT_GRIDS, mods: TAROT_MODS },
};

const clampIdx = (i: number, len: number) => Math.min(len - 1, Math.max(0, i));

/** themeId 有专属表就查表；否则 cityIndex = 城市按 order 排好后的下标 */
export function levelGrid(themeId: string, cityIndex: number, unlockOrder: number): Grid {
  const t = THEME_LEVELS[themeId];
  if (t) return { ...t.grids[clampIdx(unlockOrder, t.grids.length)] };
  return square(levelSize(cityIndex, unlockOrder));
}

export function themeLevelMods(themeId: string, cityIndex: number, unlockOrder: number): LevelMods {
  const t = THEME_LEVELS[themeId];
  if (t) return { ...t.mods[clampIdx(unlockOrder, t.mods.length)] };
  return levelMods(cityIndex, unlockOrder);
}

/**
 * 每日挑战抽到竖版图（塔罗）时，把当天的 n×n 换成块数相近、格子接近正方的竖版格：
 * 5 → 4×6，6 → 5×7，7 → 6×9，8 → 7×10。横竖比 ≥ 0.9 的图（城市）照旧 n×n。
 */
export function dailyGrid(n: number, aspect: number): Grid {
  if (aspect >= 0.9 && aspect <= 1.1) return square(n);
  const cols = Math.max(3, n - 1);
  // 取不超过图比例的行数（格子略高不略扁）：2:3 时 4→6、5→7、6→9、7→10
  const rows = Math.max(cols, Math.floor(cols / aspect + 0.01));
  return { cols, rows: Math.min(MAX_SIDE, rows) };
}

/**
 * 每日挑战的玩法按星期循环 无 → 旋转 → 翻面（每天固定，与每日种子一样全天同一题）：
 * 周一 无、周二 旋转、周三 翻面、周四 无、周五 旋转、周六 翻面、周日 无。
 */
export const DAILY_MODS: readonly LevelMods[] = [NONE, NONE, ROT, FLIP, NONE, ROT, FLIP]; // 下标 = getUTCDay()，0 = 周日

export function dailyMods(date: string): LevelMods {
  const [y, m, d] = date.split('-').map(Number);
  return { ...DAILY_MODS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] };
}
